import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import get from 'lodash/get';

import {
  Box,
  Card as MuiCard,
  Divider,
  Typography,
  styled,
} from '@mui/material';
import { makeStyles, useTheme } from '@mui/styles';

import Interweave from 'interweave';
import isEmpty from 'lodash/isEmpty';
import CbButton from '../../components/Buttons/CbButton';
import { useAPIErrorHandler } from '../../components/hooks/useAPIErrorHandler';
import { getAccount } from '../../api/accounts/accounts.api';
import { useActor } from '../../components/hooks/useActor';
import { addConnector } from '../../api/connectors/connectors.api';
import { getData } from '../../utils/functional/fp';
import { CONNECTOR_TYPES } from '../connectors/connectors.constants';
import { usePersona } from '../../components/hooks/usePersona';
import { agencyDetails } from '../../agencies/AgencyService';
import { Show } from '../../components/Show';
import useWizerQuery from './useWizerQuery';
import { useAuth } from '../../components/hooks/useAuth';
import { WizerActivationModalConfig } from './WizerActivationModal';
import { Modal } from '../../components/modals/v2/helpers/v2.modal.helpers';
import { getConnections } from '../connectors/ConnectorService';
import useNotifications from '../../_lib/notifications/useNotifications';

interface TrainingProps {
  trainingAccess: {
    disabled: boolean;
    disabledTooltip: string;
  };
}

export const Training = ({ trainingAccess }: TrainingProps) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const handleAPIError = useAPIErrorHandler();
  const classes = useStyles();
  const persona = usePersona();
  const actor = useActor();
  const accountId = actor.account.id;
  const theme = useTheme();
  const history = useHistory();
  const { isCowbellUserJumped } = useAuth();

  const [error, setError] = React.useState({ code: '', message: '' });

  const AGENCY_LICENSES = 30;

  const accountDetailsQuery = useQuery({
    queryFn: () => {
      if (persona.isInsured && !accountId) {
        return null;
      }

      const getAccountDetails = persona.isAgency
        ? () => agencyDetails()
        : () => getAccount(persona.type, accountId);

      return getAccountDetails();
    },
    queryKey: ['account-details', { accountId, persona: persona.type }],
    select: getData,
    onError: handleAPIError('Failed to retrieve account details'),
  });

  const { data: connections, isLoading: isConnectionsLoading } = useQuery({
    queryKey: ['/api/connector/v1/connections', { accountId }],
    queryFn: () => getConnections(),
    select: getData,
  });

  const isWizerConnected =
    connections?.content?.some(
      ({ connectorType, connectorStatus }) =>
        connectorType === CONNECTOR_TYPES.WIZER && connectorStatus === 'SUCCESS'
    ) ?? false;

  const {
    data: wizer,
    isLoading: isWizerLoading,
    error: wizerError,
  } = useWizerQuery({
    cbid: (accountDetailsQuery.data as any)?.cbid,
    email: actor.email,
    accountId,
  });

  const isLoading = accountDetailsQuery.isLoading || isWizerLoading;

  const handleEnableTraining = () => {
    const params = {
      connector: CONNECTOR_TYPES.WIZER,
      provider: 'COWBELL',
      credentials: {
        firstName: actor.firstName,
        lastName: actor.lastName,
        email: actor.email,
        companyName: actor.account.name,
        licensesToAllocate:
          (accountDetailsQuery.data as any)?.noOfEmployeesAll ??
          AGENCY_LICENSES,
      },
      cbid: (accountDetailsQuery.data as any)?.cbid,
      metadata: null,
    };

    return addConnector({ data: params })
      .then(() => {
        queryClient.invalidateQueries(['wizer-training']);
        notifications.success(
          `Training was successfully enabled for ${actor.account.name}`
        );
      })
      .catch(({ response }) => setError(response?.data));
  };

  const handleMyTraining = () => {
    if (wizer?.dashboardMagicLink) {
      window.open(wizer?.dashboardMagicLink, '_blank');
    }
  };

  const handleManageTraining = () => {
    if (wizer?.listTrainingMagicLink) {
      window.open(wizer?.listTrainingMagicLink, '_blank');
    }
  };

  const handleInviteUsers = () => {
    if (wizer?.addUserMagicLink) {
      window.open(wizer?.addUserMagicLink, '_blank');
    }
  };

  const hasLinks = Boolean(wizer?.dashboardMagicLink);
  const hasMessage = Boolean(error?.message);

  const formMethods = useForm();

  const handleActivateWizerForPolicyholder = () => {
    Modal.show(WizerActivationModalConfig, {
      data: {
        accountId,
        onClose: () => Modal.hide(),
      },
    });
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(handleEnableTraining)}>
        <div className="flex--spaced">
          <h1 className={`table-heading ${classes.headingMargins}`}>
            Training
          </h1>
          <div className="flex--spaced" style={{ width: '60%' }} />
        </div>
        <MuiCard style={{ height: '100%' }}>
          <Box pl={3} pb={3} pr={3}>
            <Box
              className={classes.headerContainer}
              display="flex"
              justifyContent="space-between"
              alignItems="baseline"
            >
              <h2 className={classes.headings}>
                Cowbell Sponsored Wizer Training
              </h2>
              <a
                href="https://www.wizer-training.com/"
                className={classes.wizerLogo}
                target="_blank"
                rel="noreferrer"
              >
                <img src={theme.images.wizerTraining.logo} alt="wizer logo" />
              </a>
            </Box>
            <Divider
              style={{ marginBottom: '1.21rem', marginTop: '.531rem' }}
              variant="fullWidth"
            />
            <Box display="flex" alignItems="center" flexDirection="column">
              <h2 className={`contrast-text ${classes.title}`}>Hi 👋</h2>
              <h2 className={`contrast-text ${classes.title}`}>
                Let's set up your basic training!
              </h2>
              <Show when={hasMessage}>
                <ErrorBox mt={2} mb={0} width="50%">
                  <Typography variant="caption">
                    <Interweave content={error?.message} />
                  </Typography>
                </ErrorBox>
              </Show>
              <Show when={!!wizerError}>
                <ErrorBox mt={2} mb={0} width="50%">
                  <Typography variant="caption">
                    {get(
                      wizerError,
                      'response.data.message',
                      'There was an error enabling training. Please try again later.'
                    )}
                  </Typography>
                </ErrorBox>
              </Show>
              <br />
              <iframe
                width="50%"
                height="400px"
                src="https://www.youtube.com/embed/5WM7xkhJKts"
                title="Why is security awareness so important?"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
              <Show when={!hasMessage}>
                {hasLinks && (
                  <Box mt={2} mb={0} width="50%">
                    <CbButton
                      styleName="ctaButton"
                      loading={isLoading || formMethods.formState.isSubmitting}
                      disabled={isLoading || formMethods.formState.isSubmitting}
                      styles={{ width: '100%' }}
                      type="submit"
                    >
                      Training Enabled
                    </CbButton>
                  </Box>
                )}
                {!hasLinks && !trainingAccess?.disabled && (
                  <Box mt={2} mb={0} width="50%">
                    {isCowbellUserJumped ? (
                      <CbButton
                        styleName="ctaButton"
                        onClick={handleActivateWizerForPolicyholder}
                        styles={{ width: '100%' }}
                        disabled={isWizerConnected || isConnectionsLoading}
                      >
                        Activate Wizer for Policyholder
                      </CbButton>
                    ) : (
                      <CbButton
                        styleName="ctaButton"
                        loading={
                          isLoading || formMethods.formState.isSubmitting
                        }
                        disabled={
                          isLoading || formMethods.formState.isSubmitting
                        }
                        styles={{ width: '100%' }}
                        type="submit"
                      >
                        Enable Training
                      </CbButton>
                    )}
                  </Box>
                )}

                {!hasLinks &&
                  trainingAccess?.disabled &&
                  !isWizerLoading &&
                  isEmpty(wizerError?.message) && (
                    <>
                      <ErrorBox mt={1} mb={0} width="50%">
                        <Typography variant="caption">
                          {trainingAccess?.disabledTooltip}
                        </Typography>
                      </ErrorBox>
                      <Box mt={0} mb={0} width="50%">
                        <CbButton
                          styleName="primary"
                          onClick={() =>
                            history.push('/customer/resilience/catalog')
                          }
                        >
                          Go to Catalog
                        </CbButton>
                      </Box>
                    </>
                  )}
                {!trainingAccess?.disabled && (
                  <Box
                    mt={2}
                    width="50%"
                    display="flex"
                    justifyContent="space-between"
                  >
                    <CbButton
                      styleName="ctaButton"
                      onClick={handleMyTraining}
                      styles={{ width: '32%' }}
                      disabled={!hasLinks}
                    >
                      My Training
                    </CbButton>
                    <CbButton
                      styleName="ctaButton"
                      onClick={handleManageTraining}
                      styles={{ width: '32%' }}
                      disabled={!hasLinks}
                    >
                      Manage Training
                    </CbButton>
                    <CbButton
                      styleName="ctaButton"
                      onClick={handleInviteUsers}
                      styles={{ width: '32%' }}
                      disabled={!hasLinks}
                    >
                      Invite Users
                    </CbButton>
                  </Box>
                )}
              </Show>
            </Box>
          </Box>
        </MuiCard>
      </form>
    </FormProvider>
  );
};

const ErrorBox = styled(Box)(({ theme }) => ({
  color: theme.agencyDash.text.error,
  a: {
    color: theme.config.colors.cowbellBlue,
  },
}));

const useStyles = makeStyles(() => ({
  headingMargins: {
    marginBottom: '0.8333333333rem',
    marginTop: '-4px',
  },
  headerContainer: {
    height: '1.75rem',
    width: '60.625rem',
  },
  headings: {
    fontFamily: 'Titillium Web',
    fontSize: '1.25rem',
    fontStretch: 'normal',
    fontStyle: 'normal',
    fontWeight: 'normal',
    letterSpacing: 'normal',
    lineHeight: 1.35,
    marginTop: '.5rem',
    textAlign: 'left',
  },
  title: {
    margin: 0,
  },
  wizerLogo: {
    height: '1.69375rem',
    marginBottom: '.531rem',
    marginTop: '.3125rem',
    position: 'absolute',
    right: '2.55rem',
    width: '5.83125rem',
  },
}));
